/**
 * CustomerRail (360-B "Profile rail", picked by Seth on the idea-session
 * canvas 2026-09-08) — everything about the CUSTOMER lives in one sticky
 * column on the left: the property photo + address, which property you're
 * viewing (and the switcher), WATCH FOR, MONEY (balance + plan), PEOPLE.
 * The work — the name card, next/last visit, the tabs — gets the rest of
 * the page. Built to the approved board; nothing is arranged differently.
 *
 * House rule (Sept 8, twice): nothing that drops down lives inside an
 * overflow-hidden box. The rail card is NOT overflow-hidden — the photo
 * rounds its own corners — and the property switcher expands IN PLACE.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Check, ChevronDown, ChevronUp, DollarSign, History, MapPin, MessageSquare, Mail, Pencil, Plus, UserRound, Users } from "lucide-react";
import { cn } from "@/lib/cn";
import { CardHeader } from "@/components/ui/CardHeader";
import { displayCustomerName } from "@/lib/format";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { Copyable, mailingLabel } from "@/components/Copyable";
import { useLocationOwnership } from "@/features/jobs/properties";
import type { CustomerWithLocations, LocationRow } from "./api";
import { useCustomerStatusFlag, useCustomerBalance, isDoNotService, useLocationLifetimes } from "./api";
import { StreetViewThumb } from "./CustomerAvatar";
import { BillingPane, AddPropertyModal, MovePropertyModal } from "./CustomerHeader";
import { useContacts, ContactModal, type ContactRow } from "./CustomerContacts";
import { PeopleAndLinesDrawer } from "./PeopleAndLinesDrawer";
import { buildLines, splitLinesByEra, contactEraOf, type Line } from "./contactLines";
import { useEditCustomer } from "./editCustomerContext";
import { PreviousOwnersModal } from "./CustomerOwnerTransfer";
import { ComposeEmailModal, ComposeTextModal } from "./CustomerMessages";
import { WatchOutsBlock } from "./WatchOutsBlock";

interface Props {
  customer: CustomerWithLocations;
  selectedLocation: LocationRow | null;
  onSelectLocation: (loc: LocationRow) => void;
  className?: string;
}

const money = (v: number) => v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const p10 = (s: string) => s.replace(/\D/g, "").slice(-10);
const prettyPhone = (s: string) => { const d = p10(s); return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : s; };

export function CustomerRail({ customer, selectedLocation, onSelectLocation, className }: Props) {
  const navigate = useNavigate();
  const locations = customer.locations ?? [];
  const activeLocations = locations.filter((l) => !l.location_inactive);
  const displayName = displayCustomerName(null, customer.fullname); // RECORD-READ-MODEL: the correction is already in fullname
  const { data: owned } = useLocationOwnership(customer.custno, selectedLocation?.locno ?? null);
  const currentOwner = owned?.currentOwner ?? null;
  const { data: statusFlag } = useCustomerStatusFlag(customer.custno);
  const dnsActive = isDoNotService(statusFlag, customer.credit_rating);
  const dnsFromEsc = !statusFlag?.do_not_service && dnsActive;
  const pegasusInactive = statusFlag?.inactive === true;

  const [tileOk, setTileOk] = useState(true);
  const [addrView, setAddrView] = useState<"service" | "billing">("service");
  const norm = (s?: string | null) => (s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const billingDiffers = !!customer.add1 && !!selectedLocation && norm(customer.add1) !== norm(selectedLocation.add1);

  return (
    <aside className={cn("rounded-2xl border border-hairline bg-white shadow-e2", className)}>
      {/* ------------------------------------------------------------ photo */}
      {selectedLocation ? (
        <div className="relative h-[200px] overflow-hidden rounded-t-2xl">
          <StreetViewThumb address={selectedLocation} onAvailability={setTileOk} className="absolute inset-0" />
          {!tileOk && <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-700 to-brand-900" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
          {billingDiffers && (
            <button
              type="button"
              onClick={() => setAddrView((v) => (v === "service" ? "billing" : "service"))}
              className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-amber-400/95 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-950 shadow-e1"
              title="This account bills at a different address — click to flip between the two"
            >
              {addrView === "service" ? "bills elsewhere" : "back to service address"}
            </button>
          )}
          <div className="absolute inset-x-0 bottom-0 p-3.5">
            {addrView === "service" ? (
              <Copyable
                block
                label="Copy full address"
                iconClassName="text-white/60 hover:bg-white/20 hover:text-white"
                text={mailingLabel({ name: currentOwner?.name ?? displayName, address: selectedLocation.add1, city: selectedLocation.city, state: selectedLocation.state, zip: selectedLocation.zip })}
              >
                {currentOwner ? (
                  <div className="text-xs font-medium text-white/80">{currentOwner.name}</div>
                ) : selectedLocation.locname && selectedLocation.locname !== selectedLocation.add1 && displayCustomerName(selectedLocation.locname, null) !== displayName ? (
                  <div className="text-xs font-medium text-white/80">{selectedLocation.locname}</div>
                ) : null}
                {selectedLocation.add1 && (
                  <div className="text-xl font-bold leading-tight tracking-tight text-white drop-shadow-sm">{selectedLocation.add1}</div>
                )}
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-1">
                  {(selectedLocation.city || selectedLocation.state) && (
                    <span className="text-[13px] text-white/85">
                      {selectedLocation.city}{selectedLocation.city && selectedLocation.state && ", "}{selectedLocation.state} {selectedLocation.zip}
                    </span>
                  )}
                  {selectedLocation.add1 && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([selectedLocation.add1, selectedLocation.city, selectedLocation.state, selectedLocation.zip].filter(Boolean).join(", "))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur transition-colors hover:bg-white/25"
                    >
                      <MapPin className="h-3 w-3" /> Maps
                    </a>
                  )}
                </div>
              </Copyable>
            ) : (
              <BillingPane customer={customer} location={selectedLocation} differs={billingDiffers} compact />
            )}
          </div>
        </div>
      ) : (
        <div className="flex h-[120px] items-center justify-center rounded-t-2xl bg-gradient-to-br from-slate-800 via-slate-700 to-brand-900 text-sm text-white/70">
          No service address on file
        </div>
      )}

      {/* ------------------------------------------------------------ property */}
      <PropertyRow custno={customer.custno} locations={activeLocations} selected={selectedLocation} onSelect={onSelectLocation} />

      {/* ------------------------------------------------------------ watch for */}
      <WatchOutsBlock
        className="border-b border-hairline"
        customerName={currentOwner?.name ?? displayName}
        custno={customer.custno}
        locno={selectedLocation?.locno ?? null}
        dns={dnsActive}
        dnsReason={statusFlag?.dns_reason ?? (dnsFromEsc && customer.credit_rating ? `ESC credit rating ${customer.credit_rating}` : null)}
        inactiveReason={pegasusInactive && !customer.customerinactive ? (statusFlag?.reason ?? "") : undefined}
      />

      {/* ------------------------------------------------------------ money */}
      <MoneyBlock custno={customer.custno} multiProperty={locations.length > 1} onInvoices={() => navigate(`/customers/${customer.custno}?tab=invoices`)} />

      {/* ------------------------------------------------------------ people */}
      <PeopleBlock customer={customer} location={selectedLocation} />
    </aside>
  );
}

// PROP-LTV-01: the per-property lifetime hook moved to ./api (HDR-TWEAKS-01)
// so the HEADER can read it too — CustomerRail imports FROM CustomerHeader,
// so anything the header needs must live below both.

/** $193,642 → "$194K" — picker rows have no room for cents. */
export const compactMoney = (v: number) =>
  v >= 999500 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1000 ? `$${Math.round(v / 1000)}K` : `$${Math.round(v)}`;

// ----------------------------------------------------------------------------
// PropertyRow — "1 property · service address · Change ▾". Click → the list
// expands IN PLACE (search, scroll, Add a property, Move) — never a dropdown.
// C360-BOARD-01: exported + `bare` renders the open list only (no header row)
// so the hero header's "N properties ▾" pill can drop the SAME switcher.
// ----------------------------------------------------------------------------
export function PropertyRow({ custno, locations, selected, onSelect, bare }: {
  custno: string;
  locations: LocationRow[];
  selected: LocationRow | null;
  onSelect: (loc: LocationRow) => void;
  /** Render the expanded list only — the caller owns the toggle. */
  bare?: boolean;
}) {
  const [open, setOpen] = useState(!!bare);
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(12);
  const [adding, setAdding] = useState(false);
  const [moving, setMoving] = useState<LocationRow | null>(null);
  // PROP-LTV-01: each row shows what THAT property has billed, lifetime —
  // only fetched once the list is open, and only worth showing when the
  // account actually has more than one property to compare.
  const { data: lifetimes } = useLocationLifetimes(open && locations.length > 1 ? custno : undefined);
  const q = query.trim().toLowerCase();
  const filtered = q
    ? locations.filter((l) => [l.add1, l.locname, l.city, l.zip].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)))
    : locations;
  const shown = filtered.slice(0, limit);
  const n = locations.length;
  const idx = selected ? locations.findIndex((l) => l.locno === selected.locno) : -1;

  return (
    <div className={bare ? undefined : "border-b border-hairline"}>
      {!bare && <div className="flex items-center gap-2 px-3.5 py-2.5 text-xs">
        <Building2 className="h-3.5 w-3.5 shrink-0 text-brand-600" />
        <span className="min-w-0 flex-1 truncate">
          <b className="text-slate-800">{n} propert{n === 1 ? "y" : "ies"}</b>
          <span className="text-slate-500">{n > 1 && idx >= 0 ? ` · viewing ${idx + 1} of ${n}` : " · service address"}</span>
        </span>
        <button
          type="button"
          onClick={() => { setOpen((v) => !v); setQuery(""); setLimit(12); }}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-hairline bg-white px-2 py-1 text-xs font-semibold text-slate-700 shadow-e1 transition-colors hover:bg-slate-50"
        >
          {n > 1 ? "Change" : "Add"} {open ? <ChevronUp className="h-3 w-3 text-slate-400" /> : <ChevronDown className="h-3 w-3 text-slate-400" />}
        </button>
      </div>}
      {open && (
        <div className="border-t border-slate-100 bg-slate-50/60">
          {n > 1 && (
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search address, building, city…"
              autoFocus
              className="w-full border-b border-slate-100 bg-white px-3.5 py-2 text-xs focus:outline-none"
            />
          )}
          <ul
            className="max-h-64 divide-y divide-slate-100 overflow-auto"
            onScroll={(e) => { const el = e.currentTarget; if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60 && limit < filtered.length) setLimit((cur) => cur + 24); }}
          >
            {shown.length === 0 ? (
              <li className="px-3.5 py-3 text-center text-xs text-slate-400">No property matches “{query}”</li>
            ) : shown.map((l) => (
              <li key={l.locno}>
                {/* NAV-360-01 / Exhibit C: real menu rows — pin tile, two
                    lines, 52px targets, check on the selected building. */}
                <button
                  type="button"
                  onClick={() => { onSelect(l); setOpen(false); }}
                  className={cn("flex min-h-[52px] w-full items-center gap-3 px-3.5 py-2 text-left transition-colors hover:bg-slate-100/70", selected?.locno === l.locno && "bg-brand-50")}
                >
                  <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg", selected?.locno === l.locno ? "bg-brand-100 text-brand-700" : "bg-brand-50 text-brand-600")}>
                    <MapPin className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold text-slate-800">
                      {l.add1 ?? l.locname ?? `Location ${l.locno}`}
                    </span>
                    {/* 360-POLISH-03: the city matches the street's size here
                        too — weight and color keep the street on top. */}
                    <span className="block truncate text-[13px] text-slate-500">
                      {[l.locname && l.locname !== l.add1 ? l.locname : null, l.city].filter(Boolean).join(" · ") || "—"}
                    </span>
                  </span>
                  {l.peg && <span className="shrink-0 rounded bg-sky-100 px-1 py-0.5 text-[11px] font-bold text-sky-700">Pegasus</span>}
                  {(() => {
                    const lt = lifetimes?.get(l.locno);
                    return lt && lt.total > 0 ? (
                      <span
                        className="shrink-0 text-[11px] font-bold tabular-nums text-emerald-700"
                        title={`${money(lt.total)} billed at this property · ${lt.invoices} invoice${lt.invoices === 1 ? "" : "s"}${lt.firstYear ? ` · since ${lt.firstYear}` : ""}`}
                      >
                        {compactMoney(lt.total)}
                      </span>
                    ) : null;
                  })()}
                  {selected?.locno === l.locno && <Check className="h-4 w-4 shrink-0 text-brand-700" strokeWidth={2.6} />}
                  {l.peg && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); setMoving(l); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setMoving(l); } }}
                      title="Move this property under a different customer"
                      className="shrink-0 rounded-md border border-hairline bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-500 hover:bg-slate-50"
                    >
                      Move
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          {filtered.length > shown.length && (
            <div className="border-t border-slate-100 px-3.5 py-1.5 text-[11px] text-slate-400">{shown.length} of {filtered.length} — scroll for more, or type to jump to one.</div>
          )}
          <button type="button" onClick={() => setAdding(true)} className="flex w-full items-center gap-1.5 border-t border-slate-100 px-3.5 py-2 text-xs font-semibold text-brand-700 transition-colors hover:bg-brand-50/50">
            <Plus className="h-3.5 w-3.5" /> Add a property under this customer
          </button>
        </div>
      )}
      {adding && <AddPropertyModal custno={custno} onClose={() => setAdding(false)} />}
      {moving && <MovePropertyModal loc={moving} onClose={() => setMoving(null)} />}
    </div>
  );
}

// ----------------------------------------------------------------------------
// MoneyBlock — the balance (red when owed, emerald when paid up) and the plan
// on one card, so "does this customer owe money / are they a member" is a
// glance, not a hunt (Seth, Sept 8: "no idea at quick glance if the customer
// owes money"). Balance and plan follow the CUSTOMER — all properties.
// ----------------------------------------------------------------------------
// 360-PLAN-TILE (2026-09-17): the plan line left this block — the Plan tile
// above the tabs is the one place membership shows now.
function MoneyBlock({ custno, multiProperty, onInvoices }: { custno: string; multiProperty: boolean; onInvoices: () => void }) {
  const { data: bal, isLoading: bLoading } = useCustomerBalance(custno);
  const owes = (bal?.total ?? 0) > 0.005;
  const over60 = (bal?.buckets.d60 ?? 0) + (bal?.buckets.d90 ?? 0);
  const detail = !bal?.mirrored
    ? "AR not mirrored yet"
    : owes
      ? over60 > 0 ? `${money(over60)} over 60 days` : (bal.buckets.d30 ?? 0) > 0 ? `${money(bal.buckets.d30)} over 30 days` : "all current"
      : "nothing owed";
  return (
    <div className="border-b border-hairline px-3.5 py-3">
      <CardHeader
        variant="inline"
        icon={DollarSign}
        title="Money"
        chip={multiProperty ? <span className="text-xs text-slate-400" title="Owed across every property on this account">all properties</span> : undefined}
        action={{ label: "Invoices", onClick: onInvoices }}
      />
      <button type="button" onClick={onInvoices} title="Opens the Invoices tab — collect payments there" className="mt-1 flex w-full items-baseline gap-2.5 text-left">
        <span className={cn("font-display text-2xl font-bold tracking-tight tabular-nums", bLoading ? "text-slate-300" : !bal?.mirrored ? "text-slate-400" : owes ? "text-red-700" : "text-emerald-700")}>
          {bLoading ? "…" : !bal?.mirrored ? "—" : owes ? money(bal.total) : "Paid up"}
        </span>
        <span className="min-w-0 truncate text-xs text-slate-500">{bLoading ? "" : owes ? `owed · ${detail}` : detail}</span>
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------------
// PeopleBlock — every person and line on the account (Pegasus contacts first,
// then ESC's grid or phone bank), each with call / text / email in reach.
// "+ Add" saves a Pegasus contact; the pencil on an ESC line promotes it into
// one; "Manage" opens People & Lines (roles, consent, everything).
// ----------------------------------------------------------------------------
export function PeopleBlock({ customer, location }: { customer: CustomerWithLocations; location: LocationRow | null }) {
  const custno = customer.custno;
  const locno = location?.locno ?? null;
  const contacts = useContacts(custno, locno).data ?? [];
  const canEdit = useCan("contacts.edit");
  // NEW-OWNER-CONTACTS (34i87): after a recorded sale, the previous owner's
  // ESC lines leave this list and wait under Previous Owners.
  const { data: owned } = useLocationOwnership(custno, locno);
  const era = useMemo(() => contactEraOf(owned), [owned]);
  const split = useMemo(() => (era ? splitLinesByEra(customer, location, contacts, era) : null), [customer, location, contacts, era]);
  const lines = useMemo(() => split?.current ?? buildLines(customer, location, contacts), [split, customer, location, contacts]);
  const previousLines = split?.previous ?? [];
  const [prevOpen, setPrevOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState<{ contact: ContactRow | null; prefill?: { name?: string; role?: string; phone?: string; email?: string } } | null>(null);
  const [drawer, setDrawer] = useState(false);
  const edit = useEditCustomer(); // EDIT-SUITE-01: one door, the suite
  const [textTo, setTextTo] = useState<string | null>(null);
  const [emailTo, setEmailTo] = useState<string | null>(null);
  const shown = showAll ? lines : lines.slice(0, 4);

  const nameFor = (l: Line) => l.name ?? (l.role ? l.role : l.phone ? "Phone on file" : "Email on file");
  const roleFor = (l: Line) => (l.name ? l.role : null);
  const textable = (l: Line) => !!l.phone && !/home|house|land|work|office|fax|main/i.test(l.role ?? "");

  return (
    <div className="px-3.5 py-3">
      <CardHeader
        variant="inline"
        icon={Users}
        title="People"
        right={canEdit ? (
          <button type="button" onClick={() => (edit.available ? edit.open("people") : setEditing({ contact: null }))} className="inline-flex items-center gap-0.5 text-[13px] font-semibold text-brand-700 hover:text-brand-900">
            <Plus className="h-3 w-3" /> Add
          </button>
        ) : undefined}
        action={{ label: "Manage", onClick: () => (edit.available ? edit.open("people") : setDrawer(true)), title: "Every person and line — Edit Customer opens at People & Lines" }}
      />
      {lines.length === 0 ? (
        <p className="mt-1.5 text-xs text-slate-400">{era?.owner ? `No lines for ${era.owner.name} yet.` : "No phone or email lines on file."}</p>
      ) : (
        <ul className="mt-1.5 space-y-2">
          {shown.map((l) => (
            <li key={l.key} className="group/p flex items-start gap-2.5">
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                <UserRound className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1 leading-snug">
                <div className="flex items-baseline gap-1.5">
                  <span className="truncate text-[13px] font-semibold text-slate-900">{nameFor(l)}</span>
                  {roleFor(l) && <span className="shrink-0 text-[11px] font-medium text-slate-400">{roleFor(l)}</span>}
                  {l.contact && !l.contact.locno && customer.locations.length > 1 && <span className="shrink-0 text-[11px] text-slate-400" title="Travels with the customer — shows at every property">· all properties</span>}
                </div>
                {l.phone && (
                  <div className="flex items-center gap-2">
                    <Copyable bare text={`${l.phone}${l.ext ? ` x${l.ext}` : ""}`} label="Copy phone">
                      <a href={`tel:${l.phone.replace(/[^0-9+]/g, "")}`} className="whitespace-nowrap text-[13px] font-medium tabular-nums text-brand-700 underline-offset-2 hover:underline">
                        {prettyPhone(l.phone)}{l.ext ? ` x${l.ext}` : ""}
                      </a>
                    </Copyable>
                    {textable(l) && (
                      <button type="button" onClick={() => setTextTo(l.phone!)} title="Text from Pegasus" className="rounded p-0.5 text-brand-600 hover:bg-brand-50"><MessageSquare className="h-3.5 w-3.5" /></button>
                    )}
                    {l.email && (
                      <button type="button" onClick={() => setEmailTo(l.email!)} title={`Email ${l.email} from Pegasus`} className="rounded p-0.5 text-brand-600 hover:bg-brand-50"><Mail className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                )}
                {l.email && (
                  <Copyable bare text={l.email} label="Copy email">
                    <button type="button" onClick={() => setEmailTo(l.email!)} title={`Email ${l.email} from Pegasus`} className="block max-w-full truncate text-left text-xs text-slate-500 hover:text-brand-700">
                      {l.email}
                    </button>
                  </Copyable>
                )}
              </div>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setEditing(l.contact ? { contact: l.contact } : { contact: null, prefill: { name: l.name ?? undefined, role: l.role ?? undefined, phone: l.phone ?? undefined, email: l.email ?? undefined } })}
                  title={l.contact ? "Edit this person" : "Edit in Pegasus — saves as a Pegasus contact you fully control (the ESC line stays as-is until cutover)"}
                  className="mt-0.5 shrink-0 rounded p-0.5 text-slate-300 opacity-0 transition-opacity hover:text-slate-600 focus-visible:opacity-100 group-hover/p:opacity-100"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {lines.length > 4 && (
        <button type="button" onClick={() => setShowAll((v) => !v)} className="mt-1.5 text-xs font-medium text-brand-600 hover:text-brand-700">
          {showAll ? "Show fewer" : `Show all ${lines.length}`}
        </button>
      )}
      {era && previousLines.length > 0 && (
        <button type="button" onClick={() => setPrevOpen(true)} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-brand-700" title="Who ESC still lists for this address — the previous owner's lines">
          <History className="h-3 w-3" /> Previous owner · {previousLines.length} line{previousLines.length === 1 ? "" : "s"}
        </button>
      )}
      {prevOpen && (
        <PreviousOwnersModal previousOwners={owned?.previousOwners ?? []} escLines={previousLines} onClose={() => setPrevOpen(false)} />
      )}
      {editing && (
        <ContactModal custno={custno} locno={locno} contact={editing.contact} prefill={editing.prefill} onClose={() => setEditing(null)} />
      )}
      {drawer && (
        <PeopleAndLinesDrawer open={drawer} onClose={() => setDrawer(false)} customer={customer} location={location} onText={(p) => setTextTo(p)} onEmail={(e) => setEmailTo(e)} />
      )}
      {textTo && <ComposeTextModal custno={custno} initialTo={textTo} onClose={() => setTextTo(null)} />}
      {emailTo && <ComposeEmailModal custno={custno} initialTo={emailTo} onClose={() => setEmailTo(null)} />}
    </div>
  );
}
